"use client";
import * as React from "react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Unlock, Phone, Mail, Lock } from "lucide-react";
import { useEffect } from "react";



type Submission = {
  fullName: string;
  email: string;
  phone: string;
  message: string;
  channel: string;
  other?: string;
  timestamp: string;
};

type PrivateSectionProps = {
  password: string;
};

function getSubmissions(): Submission[] {
  try {
    const data = window.localStorage.getItem("admin_submissions");
    if (data) {
      return JSON.parse(data);
    }
    return [];
  } catch {
    return [];
  }
}

function saveSubmission(submission: Submission) {
  try {
    const prev = window.localStorage.getItem("admin_submissions");
    let arr: Submission[] = [];
    if (prev) {
      arr = JSON.parse(prev);
    }
    arr.push(submission);
    window.localStorage.setItem("admin_submissions", JSON.stringify(arr));
  } catch {}
  window.dispatchEvent(new Event("admin_submission_added"));
}

function groupSubmissionsByDay(submissions: Submission[]) {
  const map: Record<string, number> = {};
  submissions.forEach((s) => {
    const date = new Date(s.timestamp);
    const dayStr = date.toLocaleDateString();
    if (!map[dayStr]) {
      map[dayStr] = 0;
    }
    map[dayStr]++;
  });
  const days = Object.keys(map)
    .map((day) => ({
      day,
      count: map[day],
    }))
    .sort((a, b) => {
      const ad = new Date(a.day).getTime();
      const bd = new Date(b.day).getTime();
      return ad - bd;
    });
  return days;
}

function PrivateSection({ password }: PrivateSectionProps) {
  const [input, setInput] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [error, setError] = useState("");
  const [submissions, setSubmissions] = useState<Submission[]>([]);

  useEffect(() => {
    if (!unlocked) return;

    function syncSubmissions() {
      setSubmissions(getSubmissions());
    }

    syncSubmissions();

    const interval = setInterval(syncSubmissions, 2000);

    function handleStorage(e: StorageEvent) {
      if (e.key === "admin_submissions") {
        syncSubmissions();
      }
    }
    function handleManualAdminSubmission() {
      syncSubmissions();
    }
    window.addEventListener("storage", handleStorage);
    window.addEventListener(
      "admin_submission_added",
      handleManualAdminSubmission,
    );

    return () => {
      clearInterval(interval);
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener(
        "admin_submission_added",
        handleManualAdminSubmission,
      );
    };
  }, [unlocked]);

  // Always poll immediately when unlocked
  useEffect(() => {
    if (!unlocked) return;
    setSubmissions(getSubmissions());
  }, [unlocked]);

  if (!unlocked) {
    const handleUnlock = (e: React.FormEvent) => {
      e.preventDefault();
      if (input === password) {
        setUnlocked(true);
        setError("");
      } else {
        setError("Incorrect password. Please try again.");
      }
    };

    return (
      <section className="min-h-screen flex items-center justify-center bg-background">
        <form
          onSubmit={handleUnlock}
          className="bg-card p-8 rounded-md shadow-md border flex flex-col gap-6 w-full max-w-sm"
        >
          <h2 className="text-2xl font-bold text-card-foreground mb-2 text-center">
            Private Section
          </h2>
          <input
            type="password"
            placeholder="Enter password"
            className="h-12 px-4 rounded-md border bg-card text-card-foreground"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            autoFocus
          />
          {error && (
            <span className="text-destructive text-sm text-center">
              <span>{error}</span>
            </span>
          )}
          <Button className="w-full bg-primary text-primary-foreground font-bold rounded-md hover:bg-primary/90 transition-colors">
            Unlock
          </Button>
        </form>
      </section>
    );
  }

  const chartData = groupSubmissionsByDay(submissions);
  const maxCount = chartData.reduce((m, d) => (d.count > m ? d.count : m), 0);

  return (
    <section
      className="min-h-screen flex items-center justify-center bg-background"
      id="admin"
    >
      <div className="bg-card p-8 rounded-md shadow-md border w-full max-w-4xl">
        <h2 className="text-2xl font-bold mb-4 text-green-600">
          Admin Contact Submissions
        </h2>
        <p className="text-muted-foreground mb-8">
          This is a private page. Below are the latest form submissions.
        </p>

        <div className="mb-10">
          <span className="font-semibold text-lg text-foreground mb-2 block">
            Submission Volume by Day
          </span>
          <div className="bg-muted border rounded-md p-6 flex items-end gap-2 min-h-[180px] overflow-x-auto">
            {chartData.length === 0 && (
              <div className="text-muted-foreground text-sm text-center w-full">
                No data yet.
              </div>
            )}
            {chartData.length > 0 &&
              chartData.map(({ day, count }, i) => (
                <div className="flex flex-col items-center mx-2" key={i}>
                  <div
                    className="rounded bg-primary flex items-end justify-center transition-all duration-300"
                    style={{
                      width: "24px",
                      height: maxCount
                        ? `${Math.round((120 * count) / maxCount) + 24}px`
                        : "24px",
                    }}
                  >
                    <span className="text-primary-foreground font-bold text-sm mb-1">
                      {count}
                    </span>
                  </div>
                  <span className="text-xs text-muted-foreground mt-2 break-all text-center w-16">
                    <span>{day}</span>
                  </span>
                </div>
              ))}
          </div>
        </div>

        <div className="w-full overflow-x-auto border rounded-md bg-muted p-0">
          <table className="min-w-[700px] w-full text-sm text-left">
            <thead>
              <tr className="bg-card">
                <th className="py-4 px-6 font-semibold border-b border-border text-green-600">
                  Name
                </th>
                <th className="py-4 px-6 font-semibold border-b border-border text-green-600">
                  Email
                </th>
                <th className="py-4 px-6 font-semibold border-b border-border text-green-600">
                  Contact Number
                </th>
                <th className="py-4 px-6 font-semibold border-b border-border text-green-600">
                  Message
                </th>
                <th className="py-4 px-6 font-semibold border-b border-border text-green-600">
                  Date
                </th>
              </tr>
            </thead>
            <tbody>
              {submissions.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="text-center text-muted-foreground py-12"
                  >
                    No submissions yet.
                  </td>
                </tr>
              )}
              {submissions.length > 0 &&
                submissions
                  .slice()
                  .reverse()
                  .map((s, i) => (
                    <tr key={i} className="border-b border-border">
                      <td className="py-3 px-6">
                        <span>{s.fullName}</span>
                      </td>
                      <td className="py-3 px-6">
                        <span>{s.email}</span>
                      </td>
                      <td className="py-3 px-6">
                        <span>{s.phone}</span>
                      </td>
                      <td className="py-3 px-6">
                        <span>{s.message}</span>
                      </td>
                      <td className="py-3 px-6">
                        {new Date(s.timestamp).toLocaleString()}
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

export default function Contact() {
  const [showPrivate, setShowPrivate] = useState(false);

  if (showPrivate) {
    return <PrivateSection password="4048" />;
  }

  return (
    <section
      id="contact"
      className="py-12 lg:py-[4.5rem] px-5 md:px-8 lg:px-0 w-full relative"
    >
      <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-8 lg:gap-12">
        {/* Left Content Section */}
        <div className="w-full lg:w-1/2 flex flex-col">
          <span className="text-primary mb-2 lg:text-[1.35rem]"></span>
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
            <span className="lg:text-[3.7rem]">Get in touch !</span>
          </h2>
          <p className="text-neutral-500 dark:text-foreground/80 mb-8">
            <span className="dark:text-[#ebec71] lg:text-[1.3rem]">
              We are committed to making our service as convenient as possible
              for our customers please reach out for assistance or affiliate
              related inquiries
            </span>
          </p>
          {/* Contact Information List */}
          <ul className="space-y-6">
            <li className="flex items-start gap-5">
              <div className="bg-card text-card-foreground p-4 border rounded-full">
                <Phone className="w-6 h-6 text-primary" />
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-foreground mb-2 lg:text-[1.4rem]">
                  Support Line
                </span>
                <a
                  href="#"
                  className="text-neutral-500 dark:text-foreground/80 hover:underline"
                >
                  Coming soon
                </a>
              </div>
            </li>

            <li className="flex items-start gap-5">
              <div className="bg-card text-card-foreground p-4 border rounded-full">
                <Mail className="w-6 h-6 text-primary" />
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-foreground mb-2 lg:text-[1.4rem]">
                  Email Support
                </span>
                <a
                  href="#"
                  className="text-neutral-500 dark:text-foreground/80 hover:underline"
                >
                  <span className="lg:text-[1.85rem] font-black dark:text-[#376dc4]">
                    aethersim.cs@gmail.com
                  </span>
                </a>
              </div>
            </li>
          </ul>
        </div>

        {/* Right Form Section */}
        <div className="w-full lg:w-1/2">
          <ContactForm />
        </div>
      </div>
      {/* Admin Page Button at Bottom Left */}
      <button
        onClick={() => setShowPrivate(true)}
        className="fixed left-3 bottom-3 z-50 flex items-center px-3 py-2 rounded-md bg-destructive text-destructive-foreground font-semibold shadow hover:bg-destructive/80 transition-colors w-fit text-sm"
        style={{ minWidth: 0 }}
      >
        <Lock className="w-4 h-4 mr-2" />
        <span className="font-light">Admin Page</span>
      </button>
    </section>
  );
}

function ContactForm() {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    const submission: Submission = {
      fullName,
      email,
      phone,
      message,
      channel: "contact",
      other: "Forward this inquiry directly to: aethersim.cs@gmail.com",
      timestamp: new Date().toISOString(),
    };

    // Ensure submission is saved locally before proceeding
    saveSubmission(submission);

    // To ensure admin page sees latest, force StorageEvent for all tabs (esp. when same tab is used)
    localStorage.setItem(
      "admin_submissions_last_update",
      new Date().toISOString(),
    );

    await fetch("/api/submit", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(submission),
    });

    setFullName("");
    setEmail("");
    setPhone("");
    setMessage("");
    setSubmitting(false);
    setSuccess(true);

    setTimeout(() => {
      setSuccess(false);
    }, 2200);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="relative border bg-card p-6 lg:p-8 rounded-md shadow-sm overflow-hidden"
    >
      {success && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-card/90 animate-fade-in-out">
          <div className="flex flex-col items-center">
            <svg
              className="w-16 h-16 mb-2 animate-checkmark"
              viewBox="0 0 64 64"
              fill="none"
            >
              <circle
                cx="32"
                cy="32"
                r="30"
                stroke="#22c55e"
                strokeWidth="4"
                fill="none"
                className="opacity-20"
              />
              <path
                d="M18 34 L28 44 L46 24"
                stroke="#22c55e"
                strokeWidth="5"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <animate
                  attributeName="stroke-dasharray"
                  from="0,100"
                  to="34,100"
                  dur="0.45s"
                  fill="freeze"
                />
                <animate
                  attributeName="stroke-dashoffset"
                  from="34"
                  to="0"
                  dur="0.45s"
                  fill="freeze"
                />
              </path>
            </svg>
            <span className="text-green-600 text-lg font-bold mt-2">
              Submitted!
            </span>
          </div>
        </div>
      )}
      <h3 className="text-2xl font-bold text-card-foreground mb-6">
        Request Assistance
      </h3>
      <div
        className={`${success ? "pointer-events-none opacity-60" : ""} space-y-4`}
      >
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-card-foreground">
            Your Name
          </span>
          <input
            type="text"
            id="name"
            name="name"
            required
            className="h-14 px-6 rounded-md bg-card border"
            placeholder={"Enter your name"}
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            disabled={submitting || success}
          />
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-card-foreground">
            Your Email
          </span>
          <input
            type="email"
            id="email"
            name="email"
            required
            className="h-14 px-6 rounded-md bg-card border"
            placeholder={"Enter your email address"}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={submitting || success}
          />
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-card-foreground">
            Contact Number
          </span>
          <input
            type="tel"
            id="phone"
            name="phone"
            required
            className="h-14 px-6 rounded-md bg-card border"
            placeholder={"Enter your phone number"}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            disabled={submitting || success}
          />
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-card-foreground">
            Your Message
          </span>
          <textarea
            id="message"
            name="message"
            required
            className="min-h-[125px] p-6 rounded-md bg-card border"
            placeholder={"Type your message here..."}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={submitting || success}
          />
        </div>

        <Button
          className="w-fit flex justify-center items-center px-12 h-14 bg-primary text-primary-foreground rounded-md font-bold hover:bg-primary/90 transition-colors"
          disabled={submitting || success}
        >
          Send Secure Message
        </Button>
      </div>
      <style>
        {`
       @keyframes fadeInOutCheck {
      0%{ opacity: 0; }
      12%  { opacity: 1; }
      88%  { opacity: 1; }
      100% { opacity: 0; }
       }
       .animate-fade-in-out {
      animation: fadeInOutCheck 2s cubic-bezier(0.2, 0.8, 0.2, 1);
       }
       @keyframes checkmarkDraw {
      from { stroke-dasharray: 0,100; stroke-dashoffset: 34; }
      to{ stroke-dasharray: 34,100; stroke-dashoffset: 0; }
       }
       .animate-checkmark path {
      stroke-dasharray: 34,100;
      stroke-dashoffset: 0;
      animation: checkmarkDraw 0.45s cubic-bezier(0.2,0.8,0.2,1) both;
       }
         `}
      </style>
    </form>
  );
}
